import { useMemo, useRef, useState } from 'react';
import { createService } from '@busy/api';
import { ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import { useT } from '../../lib/i18n';
import {
  parseServicesCsv,
  type ImportableService,
  type ParseResult,
} from '../../lib/serviceCsv';

// Two-step modal:
//   1. File picker → parse CSV → preview the services we found
//   2. User confirms → call createService for each, naming-collision-safe
//      (any service that already exists for the user gets " (imported)"
//      appended to its name on the second try, then the third tries
//      "(imported 2)", etc. so we never overwrite existing menus).
//
// All errors / warnings from the parser are surfaced before commit so
// the barber can decide whether to proceed.

export function ImportServicesModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [importing, setImporting] = useState(false);

  function pickFile() {
    fileInputRef.current?.click();
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    const text = await file.text();
    setParsed(parseServicesCsv(text));
  }

  /** Try to create the service; on duplicate-name error, retry with
   *  " (imported)" appended (then "(imported 2)" etc) up to 5 tries. */
  async function createServiceWithRename(s: ImportableService): Promise<{ ok: boolean; finalName: string; message?: string }> {
    let attempt = 0;
    let name = s.name;
    while (attempt < 6) {
      const res = await createService({
        name,
        price_cents: s.price_cents,
        is_addon: s.is_addon,
        icon: s.icon,
        phases: s.phases,
        duration_min: s.duration_min,
      });
      if (!res.error) return { ok: true, finalName: name };
      // The hooks layer signals duplicates via a 'duplicate' code OR an
      // error message containing "already exists". Either form triggers
      // the rename retry.
      const errAny = res.error as { code?: string; message?: string };
      const isDupe =
        errAny.code === 'duplicate' ||
        /already exists|duplicate/i.test(errAny.message ?? '');
      if (!isDupe) {
        return { ok: false, finalName: name, message: errAny.message };
      }
      attempt += 1;
      name = attempt === 1
        ? `${s.name} (${t('settings.cuttimer.import.importedSuffix')})`
        : `${s.name} (${t('settings.cuttimer.import.importedSuffix')} ${attempt})`;
    }
    return { ok: false, finalName: name, message: 'too-many-duplicates' };
  }

  async function handleImport() {
    if (!parsed) return;
    setImporting(true);
    let okCount = 0;
    let failCount = 0;
    const renamed: string[] = [];
    for (const s of parsed.services) {
      const res = await createServiceWithRename(s);
      if (res.ok) {
        okCount += 1;
        if (res.finalName !== s.name) renamed.push(`${s.name} → ${res.finalName}`);
      } else {
        failCount += 1;
      }
    }
    setImporting(false);
    if (okCount > 0) {
      toast(t('settings.cuttimer.import.successToast', { n: okCount }), 'success');
    }
    if (failCount > 0) {
      toast(t('settings.cuttimer.import.failToast', { n: failCount }), 'error');
    }
    if (renamed.length > 0) {
      // Console-log the rename trail so a power user can grep DevTools
      // for a record. Non-essential to the workflow.
      // eslint-disable-next-line no-console
      console.info('[Busy Barber] Services imported with rename:', renamed);
    }
    onClose();
  }

  const totalPhases = useMemo(
    () => (parsed?.services ?? []).reduce((s, x) => s + x.phases.length, 0),
    [parsed],
  );

  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={t('settings.cuttimer.import.aria')}>
      <ModalCloseButton onClose={onClose} />
      <div className="p-5 md:p-6 space-y-4">
        <header className="pr-10">
          <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
            {t('settings.cuttimer.import.eyebrow')}
          </div>
          <h2 className="font-display text-2xl text-navy-900 dark:text-gold-500 leading-tight mt-0.5">
            {t('settings.cuttimer.import.title')}
          </h2>
          <p className="text-[13px] text-ink-500 mt-1.5 leading-relaxed">
            {t('settings.cuttimer.import.body')}
          </p>
        </header>

        {/* Hidden native file input — triggered by the visible button */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFile}
          className="hidden"
        />

        {!parsed && (
          <button
            type="button"
            onClick={pickFile}
            className="w-full h-12 rounded-lg border-2 border-dashed border-line text-ink-700 font-semibold hover:border-navy-900 dark:hover:border-gold-500 transition-colors inline-flex items-center justify-center gap-2"
          >
            <UploadIcon className="w-4 h-4" />
            {t('settings.cuttimer.import.pickFile')}
          </button>
        )}

        {parsed && (
          <>
            <div className="rounded-lg border border-line bg-bg-alt p-3">
              <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-1">
                {filename}
              </div>
              <div className="text-[14px] text-ink-900">
                {t(
                  // Two-axis plural: services count AND phases count
                  // each branch independently so "1 service · 1 phase"
                  // and "5 services · 1 phase" both read naturally.
                  parsed.services.length === 1
                    ? totalPhases === 1
                      ? 'settings.cuttimer.import.summary_one_one'
                      : 'settings.cuttimer.import.summary_one_other'
                    : totalPhases === 1
                    ? 'settings.cuttimer.import.summary_other_one'
                    : 'settings.cuttimer.import.summary_other_other',
                  {
                    services: parsed.services.length,
                    phases: totalPhases,
                  },
                )}
              </div>
            </div>

            {parsed.errors.length > 0 && (
              <div className="rounded-lg border border-red-100 dark:border-red-600/40 bg-red-50 dark:bg-red-600/15 text-red-700 dark:text-red-300 p-3 text-sm space-y-1">
                {parsed.errors.map((e, i) => (
                  <div key={i}>{e}</div>
                ))}
              </div>
            )}

            {parsed.warnings.length > 0 && (
              <details className="rounded-lg border border-amber-200 dark:border-amber-600/40 bg-amber-50 dark:bg-amber-600/10 p-3 text-[12px]">
                <summary className="cursor-pointer font-semibold text-amber-800 dark:text-amber-200">
                  {t('settings.cuttimer.import.warningsTitle', { n: parsed.warnings.length })}
                </summary>
                <ul className="mt-2 space-y-1 text-amber-800 dark:text-amber-200 leading-relaxed">
                  {parsed.warnings.map((w, i) => (
                    <li key={i}>· {w}</li>
                  ))}
                </ul>
              </details>
            )}

            {parsed.services.length > 0 && (
              <div className="rounded-lg border border-line bg-surface max-h-48 overflow-y-auto">
                <ul className="divide-y divide-line">
                  {parsed.services.map((s, i) => (
                    <li key={i} className="px-3 py-2 text-[13px] flex items-center justify-between gap-3">
                      <span className="truncate">
                        <strong>{s.name}</strong>
                        {s.is_addon && (
                          <span className="text-[10px] uppercase tracking-wider text-ink-500 ml-2">
                            {t('settings.cuttimer.import.addonBadge')}
                          </span>
                        )}
                      </span>
                      <span className="text-[11px] text-ink-500 tabular-nums whitespace-nowrap">
                        {t(
                          s.phases.length === 1
                            ? 'settings.cuttimer.import.servicePhases_one'
                            : 'settings.cuttimer.import.servicePhases_other',
                          {
                            phases: s.phases.length,
                            minutes: s.duration_min,
                          },
                        )}
                        {s.price_cents > 0 && ` · $${Math.round(s.price_cents / 100)}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={() => { setParsed(null); setFilename(null); }}
                className="px-4 py-2 rounded-lg text-[14px] font-semibold text-ink-700 hover:bg-bg-alt"
              >
                {t('settings.cuttimer.import.choose')}
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={importing || parsed.services.length === 0 || parsed.errors.length > 0}
                className="px-4 py-2 rounded-lg text-[14px] font-bold bg-navy-900 text-white hover:bg-navy-800 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {importing
                  ? t('settings.cuttimer.import.importing')
                  : t('settings.cuttimer.import.confirm', { n: parsed.services.length })}
              </button>
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
